import { Alert, Stack } from "@mantine/core";
import { useEffect, useRef } from "react";
import { useDownload } from "../../../hooks/useDownload";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { useToast } from "../../../mantine/notifications/useToast";
import { neWebUrls } from "../../../ne-web-urls";
import { getErrorText } from "../../../utils/get-error-text";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { useGenerateInvoice } from "./hooks/useGenerateInvoiceMutation";
import { usePPADirectDeliveryInvoiceDrifts } from "./hooks/usePPADirectDeliveryInvoiceDrifts";
import { usePPADirectDeliveryInvoices } from "./hooks/usePPADirectDeliveryInvoices";
import { useSendInvoice } from "./hooks/useSendInvoiceMutation";
import { useVoidInvoice } from "./hooks/useVoidInvoiceMutation";
import { EMAIL_ERROR_STATES } from "./PPAInvoicePage.constants";
import type { PPAInvoiceId } from "./PPAInvoicePage.types";
import { PPAInvoiceTable } from "./PPAInvoiceTable/PPAInvoiceTable";

function PPAInvoicePage() {
  const { showToast, updateToast, showErrorToast } = useToast();
  const activeToastId = useRef<string | undefined>(undefined);
  const { data: invoices, isLoading, error } = usePPADirectDeliveryInvoices();
  const {
    data: invoiceDrifts,
    isLoading: isDriftsLoading,
    error: driftsError
  } = usePPADirectDeliveryInvoiceDrifts();
  const { download, error: pdfDownloadError } = useDownload();
  const { generateInvoiceMutation } = useGenerateInvoice();
  const { sendInvoiceMutation } = useSendInvoice();
  const { voidInvoiceMutation } = useVoidInvoice();

  useEffect(() => {
    if (pdfDownloadError) {
      showErrorToast(getErrorText(pdfDownloadError));
    }
  }, [pdfDownloadError, showErrorToast]);

  async function handleDownloadDraft(invoiceId: PPAInvoiceId) {
    return download({
      downloadUrl:
        neWebUrls.api.ppaDirectDeliveryInvoiceGenerateDraft(invoiceId),
      options: {
        neWeb: true,
        returnResponseDataDirectly: true
      }
    });
  }

  async function handleSendInvoice(invoiceId: PPAInvoiceId) {
    try {
      await sendInvoiceMutation.mutateAsync(invoiceId);

      if (!activeToastId.current) {
        const toastText = "Rechnung wurde erfolgreich versendet.";
        const toastId = showToast({
          type: "success",
          message: toastText,
          onClose: () => {
            activeToastId.current = undefined;
          }
        });
        activeToastId.current = toastId;
      } else {
        const toastText = "Rechnungen wurden erfolgreich versendet.";
        updateToast({
          id: activeToastId.current,
          message: toastText,
          autoClose: 7000
        });
      }
    } catch (error) {
      showErrorToast(getErrorText(error));
    }
  }

  async function handleGenerateInvoice(invoiceId: PPAInvoiceId) {
    try {
      await generateInvoiceMutation.mutateAsync(invoiceId);

      if (!activeToastId.current) {
        const toastText = "Rechnung wurde erfolgreich erstellt.";
        const toastId = showToast({
          type: "success",
          message: toastText,
          onClose: () => {
            activeToastId.current = undefined;
          }
        });
        activeToastId.current = toastId;
      } else {
        const toastText = "Rechnungen wurden erfolgreich erstellt.";
        updateToast({
          id: activeToastId.current,
          message: toastText,
          autoClose: 7000
        });
      }
    } catch (error) {
      showErrorToast(getErrorText(error));
    }
  }

  async function handleCancelInvoice(invoiceId: PPAInvoiceId) {
    try {
      await voidInvoiceMutation.mutateAsync(invoiceId);

      if (!activeToastId.current) {
        const toastText = "Rechnung wurde erfolgreich storniert.";
        const toastId = showToast({
          type: "success",
          message: toastText,
          onClose: () => {
            activeToastId.current = undefined;
          }
        });
        activeToastId.current = toastId;
      } else {
        const toastText = "Rechnungen wurden erfolgreich storniert.";
        updateToast({
          id: activeToastId.current,
          message: toastText,
          autoClose: 7000
        });
      }
    } catch (error) {
      showErrorToast(getErrorText(error));
    }
  }

  async function handleDownloadInvoice(invoiceId: PPAInvoiceId) {
    return download({
      downloadUrl: neWebUrls.api.ppaInvoice(invoiceId),
      options: {
        neWeb: true,
        returnResponseDataDirectly: true
      }
    });
  }

  const failedEmailCount = invoices
    ? invoices.filter(
        (invoice) =>
          invoice.email_delivery_state &&
          EMAIL_ERROR_STATES.has(invoice.email_delivery_state)
      ).length
    : 0;

  return (
    <LoadOrError
      error={error || driftsError}
      loading={isLoading || isDriftsLoading}
    >
      {invoices && (
        <Stack>
          {failedEmailCount > 0 && (
            <Alert
              color={AlertPresets.warning.color}
              icon={AlertPresets.warning.icon}
            >
              {failedEmailCount === 1
                ? "Der Versand einer E-Mail ist fehlgeschlagen. Bitte überprüfen Sie die Details und versuchen Sie es erneut."
                : `Der Versand von ${failedEmailCount} E-Mails ist fehlgeschlagen. Bitte überprüfen Sie die Details und versuchen Sie es erneut.`}
            </Alert>
          )}
          <PPAInvoiceTable
            invoiceDrifts={invoiceDrifts}
            invoices={invoices}
            onDownloadDraft={handleDownloadDraft}
            onDownloadInvoice={handleDownloadInvoice}
            onGenerateInvoice={handleGenerateInvoice}
            onSendInvoice={handleSendInvoice}
            onVoidInvoice={handleCancelInvoice}
          />
        </Stack>
      )}
    </LoadOrError>
  );
}

export { PPAInvoicePage };
